import { Card, ItemStatus } from 'components';
import { IPayment } from 'shared/services/user-subscription';
import { formatPrice } from 'utils/payments.utils';
import { useSelector } from 'react-redux';
import { IAppReducer } from 'models';
import './Item-payment-subscription.scss';

interface ItemPaymentSubscriptionProps {
  payment: IPayment;
}

const formatPaymentDate = (dateString: string): string => {
  const date = new Date(dateString);
  const datePart = date.toLocaleDateString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const timePart = date.toLocaleTimeString('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
  return `${datePart}, ${timePart}`;
};

export const ItemPaymentSubscription = ({
  payment,
}: ItemPaymentSubscriptionProps) => {
  const { branchReducer } = useSelector((state: IAppReducer) => state);

  const getStatusType = (
    status: string
  ): 'successful' | 'pending' | 'error' => {
    const statusLower = status.toLowerCase().trim();

    // Casos de éxito (verde)
    if (
      statusLower === 'successful' ||
      statusLower === 'success' ||
      statusLower === 'pagado' ||
      statusLower === 'paid' ||
      statusLower === 'completado' ||
      statusLower === 'completed' ||
      statusLower === 'aprobado' ||
      statusLower === 'approved'
    ) {
      return 'successful';
    }

    // Casos de error (rojo)
    if (
      statusLower === 'error' ||
      statusLower === 'failed' ||
      statusLower === 'fallido' ||
      statusLower === 'cancelado' ||
      statusLower === 'cancelled' ||
      statusLower === 'rechazado' ||
      statusLower === 'rejected'
    ) {
      return 'error';
    }

    // Casos pendientes (amarillo)
    if (
      statusLower === 'pending' ||
      statusLower === 'pendiente' ||
      statusLower === 'en proceso' ||
      statusLower === 'processing'
    ) {
      return 'pending';
    }

    // Si no coincide con ningún caso conocido pero el pago tiene un total > 0,
    // asumimos que es successful (verde) en lugar de error (rojo)
    if (payment.total > 0) {
      return 'successful';
    }

    // Por defecto, si no hay total, asumimos error
    return 'error';
  };

  const getStatusMessage = (status: string): string => {
    const statusType = getStatusType(status);

    if (statusType === 'successful') {
      return 'SUCCESSFUL';
    }
    if (statusType === 'pending') {
      return 'PENDIENTE';
    }
    return 'ERROR';
  };

  const hasCouponDiscount =
    payment.has_discount ||
    (payment.coupon_code && payment.coupon_code.trim() !== '') ||
    payment.coupon_amount > 0;

  // Calcular el descuento real aplicado (subtotal - total)
  // Esto es necesario porque coupon_amount puede ser un porcentaje o un monto fijo
  const calculateActualDiscount = (): number => {
    if (!hasCouponDiscount || payment.subtotal <= 0) return 0;
    const actualDiscount = payment.subtotal - payment.total;
    return actualDiscount > 0 ? actualDiscount : 0;
  };

  const actualDiscount = calculateActualDiscount();

  // Determinar si el cupón es porcentual o de monto fijo
  const isPercentageCoupon = (): boolean => {
    if (
      !hasCouponDiscount ||
      payment.subtotal <= 0 ||
      payment.coupon_amount <= 0
    ) {
      return false;
    }

    // Si coupon_amount es mayor que 100, probablemente no es un porcentaje
    if (payment.coupon_amount > 100) {
      return false;
    }

    // Calcular el descuento esperado si fuera porcentual
    const expectedDiscountIfPercentage =
      payment.subtotal * (payment.coupon_amount / 100);

    // Comparar con el descuento real (con un margen de error de 0.01 para redondeos)
    const difference = Math.abs(actualDiscount - expectedDiscountIfPercentage);
    const isPercentage = difference < 0.01;

    return isPercentage;
  };

  const couponIsPercentage = isPercentageCoupon();

  const isStripeCharge =
    payment.provider_reference_id &&
    payment.provider_reference_id.startsWith('ch_');

  const isStripeRefund =
    payment.provider_reference_id &&
    payment.provider_reference_id.startsWith('re_');

  const isPaymentIntent =
    payment.provider_reference_id &&
    payment.provider_reference_id.startsWith('pi_');

  const isStripeTransaction =
    isStripeCharge || isStripeRefund || isPaymentIntent;

  // Detectar si es un reembolso (por ID de Stripe o por monto negativo)
  const isRefund = isStripeRefund || payment.total < 0;

  const getStripeUrl = (referenceId: string): string => {
    // Determinar si estamos en producción
    const isProduction =
      window.location.hostname === 'meraki.connectasistencia.com';
    const testPrefix = isProduction ? '' : '/test';

    if (referenceId.startsWith('re_')) {
      return `https://dashboard.stripe.com${testPrefix}/refunds/${referenceId}`;
    }
    return `https://dashboard.stripe.com/payments/${referenceId}`;
  };

  return (
    <div className="item__payment__subscription__container">
      <Card>
        <div className="item__payment__subscription__content">
          <div className="item__payment__subscription__header">
            <div className="item__payment__subscription__title">
              <span className="title--bold">
                {isRefund && '↩️ '}
                {payment.detail || 'PAGO SUSCRIPCION'}
              </span>
              <span className="title--amount">
                ${formatPrice(payment.total, branchReducer.branch)}
              </span>
            </div>
          </div>
          <div className="item__payment__subscription__detail">
            {payment.provider_reference_id && (
              <span className="item__payment__subscription__id">
                #{payment.provider_reference_id}
              </span>
            )}
            <span className="item__payment__subscription__date">
              {formatPaymentDate(payment.date)}
            </span>
            {hasCouponDiscount && payment.coupon_code && actualDiscount > 0 && (
              <span className="item__payment__subscription__coupon">
                Dscto por cupón {payment.coupon_code}
                {couponIsPercentage
                  ? ` (${payment.coupon_amount}% - $${formatPrice(
                      -actualDiscount,
                      branchReducer.branch
                    )})`
                  : ` ($${formatPrice(-actualDiscount, branchReducer.branch)})`}
              </span>
            )}
          </div>
          {payment.payment_type && (
            <div className="item__payment__subscription__type">
              {payment.payment_type}
            </div>
          )}
          <div className="item__payment__subscription__footer">
            <div className="item__payment__subscription__status">
              <ItemStatus
                status={
                  isRefund
                    ? 'successful'
                    : getStatusType(payment.payment_status)
                }
                message={
                  isRefund
                    ? 'Reembolso exitoso'
                    : getStatusMessage(payment.payment_status)
                }
              />
            </div>
            {isStripeTransaction && (
              <a
                href={getStripeUrl(payment.provider_reference_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="item__payment__subscription__stripe__link"
                title="Ver detalles en Stripe"
              >
                Ver en Stripe
              </a>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
};
